import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Store, Trash2 } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { apiClient } from "../../services/apiClientService";
import {
  productHasVariantOptions,
  getStorefrontPrices,
  productDetailPathSegment,
} from "../../utils/productVariants";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";

const productVendorIdString = (product) => {
  const v = product?.vendorId;
  if (!v) return null;
  if (typeof v === "object" && v._id != null) return String(v._id);
  return String(v);
};

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const Wishlist = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [singleVendorMode, setSingleVendorMode] = useState(false);
  const [storefrontVendorId, setStorefrontVendorId] = useState(null);
  const [storeName, setStoreName] = useState("");
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { wishlistEnabled } = useFeatureFlags();

  useEffect(() => {
    const loadStorefront = async () => {
      try {
        const res = await apiClient("GET", "/api/vendors/storefront", null, {
          skipLoading: true,
        });
        const single = !!res?.singleVendor;
        setSingleVendorMode(single);
        const vendorDoc = res?.vendor;
        const vendorMongoId = vendorDoc?._id ?? vendorDoc?.id;
        if (single && vendorMongoId != null) {
          setStorefrontVendorId(String(vendorMongoId));
          setStoreName(vendorDoc.storeName || vendorDoc.name || "this shop");
        } else {
          setStorefrontVendorId(null);
          setStoreName("");
        }
      } catch {
        setSingleVendorMode(false);
        setStorefrontVendorId(null);
        setStoreName("");
      }
    };

    loadStorefront();
  }, []);

  useEffect(() => {
    if (!wishlistEnabled) {
      setWishlistItems([]);
      return;
    }
    const fetchWishlist = async () => {
      try {
        const res = await apiClient("GET", `/api/wishlist`, null, {
          skipLoading: true,
        });
        setWishlistItems(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Error loading wishlist:", error);
        setWishlistItems([]);
      }
    };

    fetchWishlist();
  }, [wishlistEnabled]);

  const visibleItems = useMemo(() => {
    if (!singleVendorMode || !storefrontVendorId) {
      return wishlistItems;
    }
    return wishlistItems.filter((p) => productVendorIdString(p) === storefrontVendorId);
  }, [wishlistItems, singleVendorMode, storefrontVendorId]);

  const hiddenOtherVendorCount = useMemo(() => {
    if (!singleVendorMode || !storefrontVendorId) return 0;
    return wishlistItems.filter((p) => {
      const vid = productVendorIdString(p);
      return vid && vid !== storefrontVendorId;
    }).length;
  }, [wishlistItems, singleVendorMode, storefrontVendorId]);

  const handleRemove = async (productId) => {
    if (!wishlistEnabled) return;
    try {
      const res = await apiClient("POST", `/api/wishlist/toggle`, { productId }, { skipLoading: true });
      const ids = (res?.wishlist || []).map((id) => id.toString());
      setWishlistItems((prev) => prev.filter((p) => ids.includes(String(p._id))));
    } catch (err) {
      console.error("Failed to remove item:", err);
    }
  };

  const handleMoveToBag = (product) => {
    if (productHasVariantOptions(product)) {
      navigate(`/products/${productDetailPathSegment(product)}`, { state: { product } });
      return;
    }
    addToCart(product);
    handleRemove(product._id);
  };

  if (!wishlistEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50/90 via-amber-50/20 to-stone-100 px-4 py-16 sm:px-6">
        <div className="mx-auto max-w-lg rounded-3xl border border-stone-200 bg-white p-10 text-center shadow-lg">
          <Heart className="mx-auto h-10 w-10 text-stone-400" strokeWidth={1.5} />
          <h1 className="mt-4 text-xl font-bold text-stone-900">Wishlist unavailable</h1>
          <p className="mt-2 text-sm text-stone-600">
            The wishlist is temporarily turned off. You can keep shopping — saved items will be
            available again when the feature is re-enabled.
          </p>
          <Link
            to="/shop"
            className="mt-8 inline-flex items-center justify-center rounded-2xl bg-orange-500 px-6 py-3 text-sm font-bold text-white hover:bg-orange-600"
          >
            Continue shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/90 via-amber-50/20 to-stone-100 px-4 py-8 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-6xl">
        <header className="mb-8 sm:mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-orange-600">Saved for later</p>
          <h1 className="mt-1 flex flex-wrap items-center gap-2 text-2xl font-extrabold tracking-tight text-stone-900 sm:text-3xl">
            <Heart className="h-8 w-8 shrink-0 fill-rose-500 text-rose-500" strokeWidth={1.5} />
            Wishlist
          </h1>
          <p className="mt-2 max-w-xl text-sm text-stone-600">
            {singleVendorMode
              ? `Showing items from ${storeName || "your storefront"}. Other sellers’ saves stay on your account but are hidden here.`
              : "Products you love from every seller you follow."}
          </p>
        </header>

        {hiddenOtherVendorCount > 0 ? (
          <div className="mb-6 rounded-2xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
            <span className="font-semibold">{hiddenOtherVendorCount}</span>{" "}
            {hiddenOtherVendorCount === 1 ? "item is" : "items are"}{" "}
            hidden in single-store mode. They will appear again if the site switches to multi-vendor.
          </div>
        ) : null}

        {wishlistItems.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-stone-200 bg-white/80 px-6 py-14 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-400">
              <Heart className="h-7 w-7" strokeWidth={1.75} />
            </div>
            <p className="mt-4 text-base font-semibold text-stone-800">Your wishlist is empty</p>
            <p className="mt-2 text-sm text-stone-500">Tap the heart on a product to save it here.</p>
            <Link
              to="/shop"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-orange-300/35 transition hover:from-orange-600 hover:to-amber-600"
            >
              <Store className="h-4 w-4" strokeWidth={2.25} />
              Browse shop
            </Link>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="rounded-3xl border border-stone-200/90 bg-white px-6 py-14 text-center shadow-md shadow-stone-200/40">
            <Store className="mx-auto h-10 w-10 text-stone-400" strokeWidth={1.75} />
            <p className="mt-4 text-base font-semibold text-stone-800">No saves for this store yet</p>
            <p className="mt-2 text-sm text-stone-600">
              You still have items from other vendors in your wishlist — they are hidden while browsing{" "}
              <span className="font-semibold text-stone-800">{storeName}</span>.
            </p>
            <Link
              to="/shop"
              className="mt-8 inline-flex items-center justify-center rounded-xl border-2 border-stone-200 bg-white px-6 py-3 text-sm font-bold text-stone-800 transition hover:bg-stone-50"
            >
              Shop {storeName}
            </Link>
          </div>
        ) : (
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visibleItems.map((product) => {
              const { salePrice: sale, listPrice: list, hasDiscount } =
                getStorefrontPrices(product);
              const hasVar = productHasVariantOptions(product);
              const desc = stripHtml(product.description);

              return (
                <li
                  key={product._id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-stone-200/90 bg-white shadow-md shadow-stone-200/30 ring-1 ring-black/[0.02] transition hover:border-orange-200/80 hover:shadow-lg"
                >
                  <Link
                    to={`/products/${productDetailPathSegment(product)}`}
                    className="relative block aspect-[4/3] bg-gradient-to-b from-stone-50 to-white p-4"
                  >
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt=""
                        className="mx-auto h-full max-h-44 w-full object-contain transition duration-300 group-hover:scale-[1.02]"
                      />
                    ) : (
                      <div className="flex h-full min-h-[10rem] items-center justify-center text-sm text-stone-400">
                        No image
                      </div>
                    )}
                  </Link>

                  <div className="flex flex-1 flex-col border-t border-stone-100 p-4 pt-3">
                    <Link
                      to={`/products/${productDetailPathSegment(product)}`}
                      className="line-clamp-2 text-base font-bold leading-snug text-stone-900 transition hover:text-orange-700"
                    >
                      {product.name}
                    </Link>
                    {desc ? (
                      <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-stone-500">{desc}</p>
                    ) : null}

                    <div className="mt-3 flex items-baseline gap-2">
                      {hasVar ? (
                        <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                          From{" "}
                        </span>
                      ) : null}
                      <span className="text-lg font-extrabold tabular-nums text-stone-900">₹{Number(sale).toFixed(0)}</span>
                      {hasDiscount ? (
                        <span className="text-sm font-medium tabular-nums text-stone-400 line-through">
                          ₹{Number(list).toFixed(0)}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-auto flex flex-wrap gap-2 pt-4">
                      <button
                        type="button"
                        onClick={() => handleRemove(product._id)}
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                        title="Remove from wishlist"
                        aria-label="Remove from wishlist"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={2.25} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveToBag(product)}
                        className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-4 text-sm font-bold text-white shadow-md shadow-orange-300/35 transition hover:from-orange-600 hover:to-amber-600 active:scale-[0.98]"
                      >
                        <ShoppingBag className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                        {hasVar ? "Pick option" : "Move to bag"}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
